import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';

/**
 * Componente para mostrar el botón de edición
 * @param {Function} onEdit - Función a ejecutar cuando se hace clic en editar
 * @param {Object} options - Opciones de configuración del botón
 * @param {string} options.position - Posición del botón ('bottom-right', 'top-right', 'bottom-left', 'top-left')
 * @param {string} options.size - Tamaño del botón ('small', 'medium', 'large')
 * @param {string} options.variant - Variante de estilo ('primary', 'secondary', 'light')
 * @param {boolean} options.showTooltip - Si mostrar tooltip
 */
const EditButton = ({ 
  onEdit, 
  options = {} 
}) => {
  // Configuración por defecto
  const {
    position = 'top-left',
    size = 'small',
    variant = 'light',
    showTooltip = true
  } = options;

  // Si no hay función de edición, no renderizar nada
  if (!onEdit || typeof onEdit !== 'function') return null;

  // Configuración de posicionamiento
  const getPositionStyles = () => {
    const baseStyles = {
      position: 'absolute',
      fontSize: size === 'small' ? '0.65rem' : size === 'medium' ? '0.75rem' : '0.85rem',
      fontWeight: 'normal',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      zIndex: 1
    };

    switch (position) {
      case 'top-right':
        return { ...baseStyles, top: '8px', right: '40px' }; // Offset para no solapar con el badge
      case 'top-left':
        return { ...baseStyles, top: '8px', left: '8px' };
      case 'bottom-left':
        return { ...baseStyles, bottom: '8px', left: '8px' };
      case 'bottom-right':
      default:
        return { ...baseStyles, bottom: '8px', right: '8px' };
    }
  };

  // Configuración de variantes de estilo
  const getVariantStyles = () => {
    switch (variant) {
      case 'primary':
        return 'badge text-primary border border-primary';
      case 'secondary':
        return 'badge text-secondary border border-secondary';
      case 'muted':
        return 'badge text-muted border border-muted';
      case 'light':
      default:
        return 'badge text-primary border border-primary';
    }
  };

  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onEdit();
  };

  return (
    <span
      className={getVariantStyles()}
      style={{
        ...getPositionStyles(),
        cursor: 'pointer',
        userSelect: 'none'
      }}
      onClick={handleClick}
      title={showTooltip ? 'Editar este dato' : ''}
      data-testid="edit-button"
    >
      <FontAwesomeIcon icon={faEdit} />
    </span>
  );
};

export default EditButton;
